"use client";

import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { getVehicleMakes, getVehicleMakesOptions } from "@/lib/api/get-makes";
import { getVehicleModels, getVehicleModelsOptions } from "@/lib/api/get-models";
import { VehicleModelGetDTO } from "@alba-cars/common-modules";

// Types
type Option = {
  value: string;
  label: string;
};

export const useUAECarFormData = (initialMake?: string) => {
  // States for form dependencies
  const [selectedMake, setSelectedMake] = useState<string>(initialMake ?? "");

  // Use React Query for API calls
  const queries = useQueries({
    queries: [
      {
        queryKey: ["vehicle-makes"],
        queryFn: () => getVehicleMakesOptions(),
      },
      {
        queryKey: ["vehicle-models", selectedMake],
        queryFn: () => {
          const dto = new VehicleModelGetDTO();
          dto.filter = {
            makes: [selectedMake],
            validate(): string[] {
              return []; // Implement proper validation if needed
            },
          };
          return getVehicleModelsOptions(dto);
        },
        enabled: !!selectedMake, // Only fetch models when make is selected
      },
    ],
  });

  const [{ data: makesOptions, isLoading: isMakesLoading }, { data: modelsOptions, isLoading: isModelsLoading }] =
    queries;

  // UAE Emirates options
  const emiratesOptions = useMemo<Option[]>(
    () => [
      { value: "dubai", label: "Dubai" },
      { value: "abu_dhabi", label: "Abu Dhabi" },
      { value: "sharjah", label: "Sharjah" },
      { value: "ajman", label: "Ajman" },
      { value: "ras_al_khaimah", label: "Ras Al Khaimah" },
      { value: "fujairah", label: "Fujairah" },
      { value: "umm_al_quwain", label: "Umm Al Quwain" },
    ],
    [],
  );

  // Mileage ranges
  const mileageOptions = useMemo<Option[]>(
    () => [
      { value: "0-50000", label: "0 - 50,000 km" },
      { value: "50000-100000", label: "50,000 - 100,000 km" },
      { value: "100000-150000", label: "100,000 - 150,000 km" },
      { value: "150000-200000", label: "150,000 - 200,000 km" },
      { value: "200000+", label: "200,000+ km" },
    ],
    [],
  );

  // Vehicle specifications common in UAE
  const specificationsOptions = useMemo<Option[]>(
    () => [
      { value: "gcc", label: "GCC Specs" },
      { value: "imported", label: "Imported" },
      { value: "american", label: "American Specs" },
      { value: "european", label: "European Specs" },
    ],
    [],
  );

  // Vehicle options
  const vehicleOptions = useMemo<Option[]>(
    () => [
      { value: "used", label: "Used" },
      { value: "new", label: "New" },
      { value: "certified_pre_owned", label: "Certified Pre-Owned" },
    ],
    [],
  );

  // Years range (typically 20 years back from current year)
  const yearOptions = useMemo<Option[]>(() => {
    const currentYear = new Date().getFullYear();
    const years: Option[] = [];
    for (let year = currentYear; year >= currentYear - 6; year--) {
      years.push({ value: year.toString(), label: year.toString() });
    }

    // Add a separator option
    years.push({ value: "other", label: "Other" });
    return years;
  }, []);

  // Create lookup maps for efficient access
  const makesLookup = useMemo(() => {
    return new Map((makesOptions ?? []).map((make) => [make.value, make]));
  }, [makesOptions]);

  const modelsLookup = useMemo(() => {
    return new Map((modelsOptions ?? []).map((model) => [model.value, model]));
  }, [modelsOptions]);

  const emiratesLookup = useMemo(() => {
    return new Map(emiratesOptions.map((emirate) => [emirate.value, emirate]));
  }, [emiratesOptions]);

  return {
    // Options arrays
    emiratesOptions,
    makesOptions: makesOptions ?? [],
    modelsOptions: modelsOptions ?? [],
    mileageOptions,
    specificationsOptions,
    vehicleOptions,
    yearOptions,

    // Lookup maps
    makesLookup,
    modelsLookup,
    emiratesLookup,

    // Loading states
    isMakesLoading,
    isModelsLoading,

    // State setters
    selectedMake,
    setSelectedMake,
  } as const;
};

// Export type for use in components
export type UAECarFormData = ReturnType<typeof useUAECarFormData>;
